import { useEffect, useMemo, useState } from 'react';
import { BEHAVIOR_HELP, BEHAVIOR_LABELS, FREQUENCY_LABELS, WEEKDAY_NAMES_ES, MONTH_NAMES_ES, type Behavior, type Cell, type Concept, type ConceptVersion, type Frequency, type PeriodInfo, type VersionTimelineEntry } from '@balance/domain';
import { api, type VersionPayload } from '../api.js';
import { centsToInput, eur, inputToCents } from '../format.js';
import { useApp } from '../state.js';
import { Modal } from './Modal.js';
import { Badge } from './ui.js';

export type WizardMode = 'create' | 'conditions' | 'reactivate';

interface WizardProps {
  mode: WizardMode;
  concept?: Concept;
  baseVersion?: ConceptVersion | null;
  presetBehavior?: Behavior;
  presetDirection?: Concept['direction'];
  presetName?: string;
  presetAmountCents?: number | null;
  presetDate?: string;
  onClose: () => void;
  onSaved: (conceptId: string) => void;
}

interface Draft {
  name: string;
  categoryId: string;
  newCategory: string;
  personId: string;
  behavior: Behavior;
  frequency: Frequency;
  weekday: number;
  referenceDate: string;
  dayOfMonth: number;
  monthOfYear: number;
  instalments: string;
  periodOffset: number;
  amount: string;
  principal: string;
  validFrom: string;
  meter: boolean;
  notes: string;
  note: string;
}

const FREQ_BY_BEHAVIOR: Record<Behavior, Frequency[]> = {
  budget: ['monthly', 'weekend'],
  fixed: ['monthly', 'bimonthly', 'annual', 'weekly'],
  variable_service: ['monthly', 'bimonthly', 'annual'],
  occurrence: ['weekly', 'biweekly', 'monthly'],
  one_off: ['once'],
  income: ['monthly', 'once', 'annual', 'biweekly'],
  loan: ['monthly'],
};

const STEP_LABELS = ['Nombre', 'Comportamiento', 'Calendario', 'Importe', 'Vigencia', 'Revisión'];

export function ConceptWizard(props: WizardProps) {
  const { catalog, balance, run, toast } = useApp();
  const today = balance?.today ?? new Date().toISOString().slice(0, 10);
  const existing = props.concept;
  const base = props.baseVersion ?? null;
  const [draft, setDraft] = useState<Draft>(() => ({
    name: existing?.name ?? props.presetName ?? '',
    categoryId: existing?.categoryId ?? '',
    newCategory: '',
    personId: existing?.personId ?? '',
    behavior: existing?.behavior ?? props.presetBehavior ?? 'fixed',
    frequency: base?.frequency ?? (existing ? FREQ_BY_BEHAVIOR[existing.behavior][0]! : props.presetBehavior ? FREQ_BY_BEHAVIOR[props.presetBehavior][0]! : 'monthly'),
    weekday: base?.schedule.weekday ?? 4,
    referenceDate: base?.schedule.referenceDate ?? props.presetDate ?? today,
    // Por defecto se cobra el mismo día del alta cada mes (las suscripciones no prorratean).
    dayOfMonth: base?.schedule.dayOfMonth ?? Number((props.presetDate ?? today).slice(8, 10)),
    monthOfYear: base?.schedule.monthOfYear ?? Number(today.slice(5, 7)),
    instalments: base?.schedule.instalments ? String(base.schedule.instalments) : '',
    periodOffset: base?.schedule.periodOffset ?? 0,
    amount: centsToInput(base?.amountCents ?? props.presetAmountCents ?? null),
    principal: centsToInput(base?.principalCents ?? null),
    validFrom: props.mode === 'create' ? props.presetDate ?? today : today,
    meter: existing?.meter ?? false,
    notes: existing?.notes ?? '',
    note: '',
  }));
  const firstStep = props.mode === 'create' ? 0 : 2;
  const [step, setStep] = useState(firstStep);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ periods: PeriodInfo[]; cells: Cell[]; timeline: VersionTimelineEntry[] } | null>(null);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const direction: Concept['direction'] = draft.behavior === 'income' ? 'income' : 'expense';
  const freqOptions = FREQ_BY_BEHAVIOR[draft.behavior];
  useEffect(() => {
    if (!freqOptions.includes(draft.frequency)) set('frequency', freqOptions[0]!);
  }, [draft.behavior]); // eslint-disable-line react-hooks/exhaustive-deps

  const versionPayload = useMemo((): VersionPayload | null => {
    const amountCents = inputToCents(draft.amount);
    if (draft.behavior !== 'budget' && amountCents === null) return null;
    const schedule: ConceptVersion['schedule'] = {};
    switch (draft.frequency) {
      case 'weekly':
        schedule.weekday = draft.weekday;
        break;
      case 'biweekly':
        schedule.referenceDate = draft.referenceDate;
        break;
      case 'monthly':
        schedule.dayOfMonth = draft.dayOfMonth;
        if (draft.behavior === 'loan') {
          schedule.referenceDate = draft.referenceDate;
          schedule.instalments = draft.instalments ? Number(draft.instalments) : null;
        }
        break;
      case 'bimonthly':
        schedule.referenceDate = draft.referenceDate;
        schedule.dayOfMonth = draft.dayOfMonth;
        break;
      case 'annual':
        schedule.monthOfYear = draft.monthOfYear;
        schedule.dayOfMonth = draft.dayOfMonth;
        break;
      case 'once':
        schedule.referenceDate = draft.referenceDate;
        if (draft.instalments && Number(draft.instalments) > 1) schedule.instalments = Number(draft.instalments);
        break;
      case 'weekend':
        break;
    }
    if (draft.periodOffset) schedule.periodOffset = draft.periodOffset;
    const validFrom = draft.frequency === 'once' && props.mode === 'create' ? draft.referenceDate : draft.validFrom;
    return {
      validFrom,
      state: 'active',
      frequency: draft.frequency,
      amountCents: amountCents ?? 0,
      schedule,
      principalCents: draft.behavior === 'loan' ? inputToCents(draft.principal) : null,
      note: draft.note || null,
    };
  }, [draft, props.mode]);

  function validate(s: number): string | null {
    if (s === 0 && !draft.name.trim()) return 'Poné un nombre al concepto.';
    if (s === 0 && catalog?.concepts.some((c) => c.name.toLowerCase() === draft.name.trim().toLowerCase() && c.id !== existing?.id)) return 'Ya existe un concepto con ese nombre.';
    if (s === 2) {
      if (draft.frequency === 'biweekly' && !draft.referenceDate) return 'Indicá la fecha de referencia.';
      if (draft.frequency === 'once' && !draft.referenceDate) return 'Indicá la fecha del gasto.';
      if (draft.instalments && (!/^\d+$/.test(draft.instalments) || Number(draft.instalments) < 1)) return 'Número de cuotas inválido.';
    }
    if (s === 3) {
      const a = inputToCents(draft.amount);
      if (a === null || a < 0) return 'Indicá un importe válido (por ejemplo 30,00).';
      if (draft.behavior === 'loan' && draft.principal && inputToCents(draft.principal) === null) return 'Importe recibido inválido.';
    }
    if (s === 4 && !/^\d{4}-\d{2}-\d{2}$/.test(draft.validFrom)) return 'Indicá la fecha desde la que rige.';
    return null;
  }

  async function next() {
    const err = validate(step);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    if (step === 4) await loadPreview();
    setStep((s) => Math.min(5, s + 1));
  }

  async function loadPreview() {
    if (!versionPayload) return;
    try {
      const versions: VersionPayload[] = [];
      if (existing && catalog) {
        for (const v of catalog.versions.filter((x) => x.conceptId === existing.id && x.validFrom < versionPayload.validFrom)) {
          versions.push({ validFrom: v.validFrom, state: v.state, frequency: v.frequency, amountCents: v.amountCents, schedule: v.schedule, principalCents: v.principalCents, note: v.note });
        }
      }
      versions.push(versionPayload);
      const exceptions = existing && catalog ? catalog.exceptions.filter((e) => e.conceptId === existing.id).map((e) => ({ kind: e.kind, originalDate: e.originalDate, newDate: e.newDate, amountCents: e.amountCents })) : [];
      setPreview(await api.preview({ concept: { id: existing?.id, name: draft.name || 'Vista previa', behavior: draft.behavior, direction }, versions, exceptions }));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo calcular la vista previa', 'error');
    }
  }

  async function save() {
    if (!versionPayload) return;
    setSaving(true);
    const result = await run(async () => {
      let categoryId: string | null = draft.categoryId || null;
      if (!categoryId && draft.newCategory.trim()) categoryId = (await api.createCategory({ name: draft.newCategory.trim() })).id;
      if (props.mode === 'create') {
        const r = await api.createConcept({ name: draft.name.trim(), categoryId, direction, behavior: draft.behavior, personId: draft.personId || null, meter: draft.meter, notes: draft.notes || null, version: versionPayload });
        return r.concept.id;
      }
      await api.addVersion(existing!.id, versionPayload);
      return existing!.id;
    }, props.mode === 'create' ? 'Concepto creado' : 'Nuevas condiciones guardadas');
    setSaving(false);
    if (result) props.onSaved(result);
  }

  const title = props.mode === 'create' ? 'Nuevo concepto' : props.mode === 'reactivate' ? `Reactivar ${existing?.name}` : `Nuevas condiciones · ${existing?.name}`;
  const amountLabel =
    draft.behavior === 'budget' ? (draft.frequency === 'weekend' ? 'Dotación por fin de semana (€)' : 'Dotación mensual (€)') : draft.behavior === 'occurrence' ? 'Precio por sesión (€)' : draft.behavior === 'variable_service' ? 'Estimación por factura (€)' : draft.behavior === 'loan' ? 'Cuota mensual de devolución (€)' : draft.behavior === 'one_off' && Number(draft.instalments) > 1 ? 'Importe de cada cuota (€)' : draft.behavior === 'income' ? 'Importe previsto (€)' : 'Importe por vencimiento (€)';

  return (
    <Modal
      title={title}
      subtitle={props.mode === 'create' ? 'Sólo se muestran las preguntas pertinentes al comportamiento elegido.' : `Las condiciones anteriores se conservan; la nueva versión rige desde la fecha indicada.`}
      onClose={props.onClose}
      side
      error={error}
      actions={
        <>
          {step > firstStep ? (
            <button type="button" className="button left" onClick={() => setStep((s) => s - 1)}>
              ← Atrás
            </button>
          ) : null}
          {step < 5 ? (
            <button type="button" className="button primary" onClick={() => void next()}>
              Continuar
            </button>
          ) : (
            <button type="button" className="button primary" disabled={saving} onClick={() => void save()}>
              {props.mode === 'create' ? 'Crear concepto' : 'Guardar condiciones'}
            </button>
          )}
        </>
      }
    >
      <div className="steps" aria-label="Pasos">
        {STEP_LABELS.map((l, i) => (
          <span key={l} className={i === step ? 'on' : i < step ? 'done' : ''} hidden={i < firstStep && props.mode !== 'create'}>
            {i + 1}. {l}
          </span>
        ))}
      </div>

      {step === 0 ? (
        <>
          <label>
            Nombre
            <input data-autofocus value={draft.name} onChange={(e) => set('name', e.target.value)} placeholder="Por ejemplo: Psicóloga Mariana" />
          </label>
          <div className="field-row">
            <label>
              Categoría
              <select value={draft.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
                <option value="">Sin categoría / nueva</option>
                {catalog?.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            {!draft.categoryId ? (
              <label>
                Nueva categoría (opcional)
                <input value={draft.newCategory} onChange={(e) => set('newCategory', e.target.value)} placeholder="Nombre de la categoría" />
              </label>
            ) : null}
          </div>
          <div className="field-row">
            <label>
              Persona (opcional)
              <select value={draft.personId} onChange={(e) => set('personId', e.target.value)}>
                <option value="">Hogar</option>
                {catalog?.persons.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="muted">La categoría sirve para encontrar el concepto; el destino presupuestario de cada movimiento se decide al imputarlo.</p>
        </>
      ) : null}

      {step === 1 ? (
        <div className="radio-list" role="radiogroup" aria-label="Comportamiento">
          {(Object.keys(BEHAVIOR_LABELS) as Behavior[]).map((b) => (
            <label key={b} className={`radio-card${draft.behavior === b ? ' on' : ''}`}>
              <input type="radio" name="behavior" checked={draft.behavior === b} onChange={() => setDraft((d) => ({ ...d, behavior: b, frequency: FREQ_BY_BEHAVIOR[b][0]! }))} />
              <span>
                <strong>{BEHAVIOR_LABELS[b]}</strong>
                <span className="muted">{BEHAVIOR_HELP[b]}</span>
              </span>
            </label>
          ))}
        </div>
      ) : null}

      {step === 2 ? (
        <>
          <div className="radio-list" role="radiogroup" aria-label="Frecuencia">
            {freqOptions.map((f) => (
              <label key={f} className={`radio-card${draft.frequency === f ? ' on' : ''}`}>
                <input type="radio" name="frequency" checked={draft.frequency === f} onChange={() => set('frequency', f)} />
                <span>
                  <strong>{FREQUENCY_LABELS[f]}</strong>
                  <span className="muted">
                    {f === 'biweekly' ? 'Cada 14 días desde una fecha de referencia; no es dos veces por mes.' : f === 'weekend' ? 'La dotación mensual = importe × fines de semana del mes.' : f === 'weekly' ? 'Tantas ocurrencias como veces caiga el día en el mes.' : f === 'once' ? 'Una sola vez; opcionalmente en cuotas mensuales.' : ''}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {draft.frequency === 'weekly' ? (
            <label>
              Día de la semana
              <select value={draft.weekday} onChange={(e) => set('weekday', Number(e.target.value))}>
                {WEEKDAY_NAMES_ES.map((n, i) => (
                  <option key={n} value={i + 1}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {draft.frequency === 'biweekly' || draft.frequency === 'bimonthly' ? (
            <label>
              Fecha de referencia (primera ocurrencia conocida)
              <input type="date" value={draft.referenceDate} onChange={(e) => set('referenceDate', e.target.value)} />
            </label>
          ) : null}
          {draft.frequency === 'monthly' || draft.frequency === 'bimonthly' || draft.frequency === 'annual' ? (
            <div className="field-row">
              <label>
                {draft.behavior === 'fixed' && draft.frequency === 'monthly' ? 'Día habitual de cobro (orientativo)' : 'Día del mes'}
                <input type="number" min={1} max={31} value={draft.dayOfMonth} onChange={(e) => set('dayOfMonth', Number(e.target.value))} />
              </label>
              {draft.frequency === 'annual' ? (
                <label>
                  Mes
                  <select value={draft.monthOfYear} onChange={(e) => set('monthOfYear', Number(e.target.value))}>
                    {MONTH_NAMES_ES.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
          {draft.frequency === 'once' ? (
            <div className="field-row">
              <label>
                Fecha
                <input type="date" value={draft.referenceDate} onChange={(e) => set('referenceDate', e.target.value)} />
              </label>
              {draft.behavior !== 'income' ? (
                <label>
                  Cuotas mensuales (1 = pago único)
                  <input type="number" min={1} max={120} value={draft.instalments || '1'} onChange={(e) => set('instalments', e.target.value)} />
                </label>
              ) : null}
            </div>
          ) : null}
          {draft.behavior === 'loan' ? (
            <div className="field-row">
              <label>
                Fecha de recepción del préstamo
                <input type="date" value={draft.referenceDate} onChange={(e) => set('referenceDate', e.target.value)} />
              </label>
              <label>
                Número de cuotas (vacío = desconocido)
                <input type="number" min={1} value={draft.instalments} onChange={(e) => set('instalments', e.target.value)} />
              </label>
            </div>
          ) : null}
        </>
      ) : null}

      {step === 3 ? (
        <>
          <label>
            {amountLabel}
            <input data-autofocus inputMode="decimal" value={draft.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0,00" />
          </label>
          {draft.behavior === 'loan' ? (
            <label>
              Importe recibido (€) · entrada de financiación
              <input inputMode="decimal" value={draft.principal} onChange={(e) => set('principal', e.target.value)} placeholder="0,00" />
            </label>
          ) : null}
          {draft.behavior === 'variable_service' ? <p className="muted">La estimación se reemplaza por el importe real de cada factura, suba o baje.</p> : null}
          {draft.behavior === 'budget' ? <p className="muted">Si el consumo supera la dotación se muestra el exceso; la dotación no se amplía sola.</p> : null}
          {draft.behavior === 'loan' ? <p className="muted">No se infiere capital pendiente restando cuotas: si no se conoce el desglose de intereses, la deuda se muestra como no calculada.</p> : null}
        </>
      ) : null}

      {step === 4 ? (
        <>
          {draft.frequency !== 'once' || props.mode !== 'create' ? (
            <label>
              Rige desde
              <input type="date" value={draft.validFrom} onChange={(e) => set('validFrom', e.target.value)} />
            </label>
          ) : (
            <p className="muted">Un gasto puntual rige desde su fecha ({draft.referenceDate}).</p>
          )}
          <label>
            Mes al que se imputa
            <select value={draft.periodOffset} onChange={(e) => set('periodOffset', Number(e.target.value))}>
              <option value={0}>El mes de la fecha</option>
              <option value={1}>El mes siguiente (p. ej. nómina de septiembre financia octubre)</option>
              <option value={-1}>El mes anterior</option>
            </select>
          </label>
          {draft.behavior === 'budget' && props.mode === 'create' ? (
            <label className="check">
              <input type="checkbox" checked={draft.meter} onChange={(e) => set('meter', e.target.checked)} />
              Mostrar medidor de consumo en la vista mensual
            </label>
          ) : null}
          <label>
            Nota de estas condiciones (opcional)
            <input value={draft.note} onChange={(e) => set('note', e.target.value)} placeholder="Por ejemplo: subida de tarifa" />
          </label>
          {props.mode === 'create' ? (
            <label>
              Notas del concepto (opcional)
              <textarea rows={2} value={draft.notes} onChange={(e) => set('notes', e.target.value)} />
            </label>
          ) : null}
        </>
      ) : null}

      {step === 5 ? (
        <>
          <div className="inline" style={{ marginBottom: 12 }}>
            <Badge>{BEHAVIOR_LABELS[draft.behavior]}</Badge>
            <Badge kind="neutral">{FREQUENCY_LABELS[draft.frequency]}</Badge>
            <Badge kind="neutral">desde {versionPayload?.validFrom}</Badge>
          </div>
          {preview ? (
            <>
              <div className="timeline">
                {preview.timeline.map((t) => (
                  <div key={t.version.id} className={t.version.state === 'inactive' ? 'inactive' : ''}>
                    <strong>{t.title}</strong> <span className="muted">desde {t.from}</span>
                    <ul className="muted">
                      {t.details.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <h3>Efecto en los próximos meses</h3>
              <table>
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="number">Previsto</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.cells.map((c) => (
                    <tr key={c.period}>
                      <td>{preview.periods.find((p) => p.period === c.period)?.label}</td>
                      <td className="number">{c.status === 'inactive' ? <span className="muted">sin vigencia</span> : eur(c.expectedCents)}</td>
                      <td className="muted">{c.occurrences.length ? `${c.occurrences.length} vencimiento(s)` : c.weekendCount !== null ? `${c.weekendCount} fines de semana` : c.status === 'none' ? '—' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="skeleton" />
          )}
        </>
      ) : null}
    </Modal>
  );
}
